import type {
  PaymentClientContextListResponse,
  PaymentCreateRequest,
  PaymentListFilterConstraints,
  PaymentListFilteredResponse,
  ProfilePaymentListFilterConstraints,
  PaymentListResponse,
  PaymentResponse,
  PaymentRetrieveOptions,
  PaymentUpdateRequest,
  PaymentConfirmRequest,
  PaymentCaptureRequest,
  PaymentCancelRequest,
  PaymentListParams,
  PaymentAttemptsListResponse,
  PaymentConnectorSettlementResponse,
  PaymentsDeletePolicyResponse,
  PaymentsDeleteResponse,
  PaymentStatusHistoryResponse,
  PaymentRoutingDecisionsResponse,
  ReplayPlaybackResponse,
  ReplaySliceResponse,
  PaymentListFiltersExt,
} from '../types';
import type { RequestExtras, RequestFn } from '../client';

/** Manage payment intents — create, confirm, capture, cancel, and list payments. */
export class Payments {
  constructor(private readonly request: RequestFn) {}

  /**
   * Create a new payment intent.
   *
   * @param params - Payment creation parameters including amount and currency.
   * @param options - Optional per-call extras: extra `headers` (e.g. an
   *   `Idempotency-Key` to make the create safe to retry), a `timeout` override,
   *   and an `AbortSignal`.
   * @returns The created payment intent.
   *
   * @example
   * ```typescript
   * const payment = await delopay.payments.create(
   *   { amount: 5000, currency: 'EUR', customer_id: 'cus_123' },
   *   { headers: { 'Idempotency-Key': 'order_1001' } },
   * );
   * ```
   *
   * @example Send `test_mode` to pick the environment per payment, so a staging
   * deploy cannot charge real cards and a forgotten processor toggle cannot
   * swallow production traffic:
   * ```typescript
   * const payment = await delopay.payments.create({
   *   amount: 5000,
   *   currency: 'EUR',
   *   test_mode: process.env.NODE_ENV !== 'production',
   * });
   * ```
   *
   * A payment that pins one connector through `routing` (the `single` form)
   * is now checked against `test_mode` here rather than at confirm: if that
   * connector has no credentials for the environment asked for, create fails
   * instead of handing back a payment whose checkout the buyer cannot
   * complete. `priority` and `volume_split` name several accounts and are
   * still resolved at confirm.
   */
  async create(params: PaymentCreateRequest, options?: RequestExtras): Promise<PaymentResponse> {
    return this.request('POST', '/payments', { body: params, ...options });
  }

  /**
   * Retrieve a payment by its ID.
   *
   * @param paymentId - The unique payment intent ID.
   * @param options - Optional query flags. `force_sync` reconciles the
   *   intent's state with the connector before returning (useful to recover
   *   a stuck intent when a webhook was lost). `all_keys_required` forces a
   *   connector sync even for intents in early states like
   *   `requires_payment_method` that would otherwise return the local
   *   snapshot. Both flags work with JWT and API-key authentication.
   * @returns The payment intent.
   *
   * @example
   * ```typescript
   * const payment = await delopay.payments.retrieve('pay_abc123');
   * const synced = await delopay.payments.retrieve('pay_abc123', {
   *   force_sync: true,
   *   all_keys_required: true,
   * });
   * ```
   */
  async retrieve(paymentId: string, options?: PaymentRetrieveOptions): Promise<PaymentResponse> {
    const path = `/payments/${encodeURIComponent(paymentId)}`;
    if (options === undefined) return this.request('GET', path);
    const query: Record<string, boolean> = {};
    if (options.force_sync !== undefined) query['force_sync'] = options.force_sync;
    if (options.all_keys_required !== undefined) {
      query['all_keys_required'] = options.all_keys_required;
    }
    if (Object.keys(query).length === 0) return this.request('GET', path);
    return this.request('GET', path, { query });
  }

  /**
   * List every attempt made on a payment, each with its full failure detail
   * (`error_code` / `error_message`, the Delopay-unified `unified_code` and
   * `unified_message`, and structured `error_details`).
   *
   * Useful for surfacing retries across connectors — e.g. "attempt 1 stripe →
   * insufficient_funds, attempt 2 adyen → success".
   *
   * `GET /payments/{paymentId}/attempts`
   *
   * @param paymentId - The payment intent ID whose attempts to list.
   * @param options - Optional per-call extras: extra `headers`, a `timeout`
   *   override, and an `AbortSignal`.
   * @returns The attempt list — `size` plus a `data` array of attempts.
   * @throws If the payment does not exist or belongs to another merchant (404).
   *
   * @example
   * ```typescript
   * const { size, data } = await delopay.payments.listAttempts('pay_abc123');
   * for (const attempt of data) {
   *   console.log(attempt.status, attempt.unified_message ?? attempt.error_message);
   * }
   * ```
   */
  async listAttempts(
    paymentId: string,
    options?: RequestExtras,
  ): Promise<PaymentAttemptsListResponse> {
    return this.request('GET', `/payments/${encodeURIComponent(paymentId)}/attempts`, options);
  }

  /**
   * The status timeline of a payment: every recorded creation / status
   * transition of the intent and its attempts, refunds and disputes, oldest
   * first. `complete: false` marks timelines partially reconstructed from
   * current records (payments created before the status log existed).
   *
   * @param paymentId - The payment intent ID.
   * @returns The ordered status-history events.
   *
   * @example
   * ```typescript
   * const { events, complete } = await delopay.payments.listStatusHistory('pay_abc123');
   * for (const event of events) {
   *   console.log(event.timestamp, event.entity_type, event.status);
   * }
   * ```
   */
  async listStatusHistory(
    paymentId: string,
    options?: RequestExtras,
  ): Promise<PaymentStatusHistoryResponse> {
    return this.request(
      'GET',
      `/payments/${encodeURIComponent(paymentId)}/status-history`,
      options,
    );
  }

  /**
   * What the payment's rail says about settling each attempt: whether the
   * money is still `pending` in the rail's hands, `available` in its balance
   * (**not** paid out), `paid_out` to a bank — with the payout's date and
   * reference — or came back as `payout_failed`; and, for the rest, which
   * kind of not-knowing applies (`unavailable`, `unknown`, `not_recorded`).
   * See {@link ConnectorSettlementStatus} for the seven values. The same
   * status for the active attempt rides on every list row and retrieve as
   * `connector_settlement_status`, and the list filters on it.
   *
   * Not the hosted-shop settlement API under `settlement.*`, which is host →
   * shop owner and manual; this is acquirer → merchant bank, per payment.
   *
   * Each attempt also carries what the rail itself took for it —
   * `processor_cost_amount` with its own `processor_cost_currency` and
   * `processor_cost_exponent`, the `processor_cost_source` that says how the
   * figure was obtained, the `processor_cost_basis` behind an estimate, and
   * the `connector_method_code` it was priced under. Recorded for **every**
   * merchant, not only those that host shops. Never present an `estimated`
   * cost as an observation, and read an absent `processor_cost_source` as
   * "nothing was ever written", which is not the `unavailable` the rail
   * answers with.
   *
   * `GET /payments/{paymentId}/settlement`
   *
   * @param paymentId - The payment intent ID.
   * @param options - Optional per-call extras: extra `headers`, a `timeout`
   *   override, and an `AbortSignal`.
   * @returns The attempts this caller may see, oldest first, each with its
   *   settlement status.
   * @throws If the payment does not exist (404), or the caller is a
   *   profile-scoped (shop) role, who is refused rather than shown a redacted
   *   body (403).
   *
   * @example
   * ```typescript
   * const { attempts } = await delopay.payments.settlement('pay_abc123');
   * for (const attempt of attempts) {
   *   if (attempt.status === 'paid_out') {
   *     console.log(attempt.paid_out_at, attempt.payout_reference);
   *   }
   *   if (attempt.processor_cost_source === 'estimated') {
   *     // Label it an estimate — it was derived from a cost schedule.
   *   }
   * }
   * ```
   */
  async settlement(
    paymentId: string,
    options?: RequestExtras,
  ): Promise<PaymentConnectorSettlementResponse> {
    return this.request('GET', `/payments/${encodeURIComponent(paymentId)}/settlement`, options);
  }

  /**
   * Update an existing payment intent before it is confirmed.
   *
   * @param paymentId - The payment intent ID to update.
   * @param params - Fields to update (amount, currency, metadata, etc.).
   * @returns The updated payment intent.
   */
  async update(paymentId: string, params: PaymentUpdateRequest): Promise<PaymentResponse> {
    return this.request('POST', `/payments/${encodeURIComponent(paymentId)}`, { body: params });
  }

  /**
   * Confirm a payment intent, triggering authorisation with the selected gateway.
   *
   * @param paymentId - The payment intent ID to confirm.
   * @param params - Confirmation parameters (payment method data, return URL, etc.).
   * @returns The updated payment intent.
   */
  async confirm(paymentId: string, params: PaymentConfirmRequest): Promise<PaymentResponse> {
    return this.request('POST', `/payments/${encodeURIComponent(paymentId)}/confirm`, {
      body: params,
    });
  }

  /**
   * Capture a previously authorised payment.
   *
   * @param paymentId - The payment intent ID to capture.
   * @param params - Optional capture parameters (partial capture amount, etc.).
   * @returns The updated payment intent.
   */
  async capture(paymentId: string, params?: PaymentCaptureRequest): Promise<PaymentResponse> {
    return this.request('POST', `/payments/${encodeURIComponent(paymentId)}/capture`, {
      body: params,
    });
  }

  /**
   * Cancel a payment intent that has not yet been captured.
   *
   * @param paymentId - The payment intent ID to cancel.
   * @param params - Optional cancellation reason.
   * @returns The updated payment intent.
   */
  async cancel(paymentId: string, params?: PaymentCancelRequest): Promise<PaymentResponse> {
    return this.request('POST', `/payments/${encodeURIComponent(paymentId)}/cancel`, {
      body: params,
    });
  }

  /**
   * List payment intents, optionally filtered by customer or date range.
   *
   * @param params - Optional filter and pagination parameters.
   * @param options - Optional per-call extras: extra `headers`, a `timeout`
   *   override, and an `AbortSignal` for cancellation.
   * @returns Paginated list of payment intents.
   *
   * @example
   * ```typescript
   * const { data } = await delopay.payments.list({ customer_id: 'cus_123', limit: 25 });
   * ```
   */
  async list(params?: PaymentListParams, options?: RequestExtras): Promise<PaymentListResponse> {
    return this.request('GET', '/payments/list', {
      query: params as Record<string, string | number | undefined>,
      ...options,
    });
  }

  /**
   * The status timeline of client/device observations captured while the
   * buyer interacted with the payment (checkout opens, confirms, redirect
   * legs, reported client signals), oldest first.
   *
   * `GET /payments/{paymentId}/client-context`
   */
  async listClientContext(
    paymentId: string,
    options?: RequestExtras,
  ): Promise<PaymentClientContextListResponse> {
    return this.request(
      'GET',
      `/payments/${encodeURIComponent(paymentId)}/client-context`,
      options,
    );
  }

  /**
   * The routing decision recorded behind each attempt of a payment: which rule
   * matched, which candidates routing produced and which filter removed each,
   * which volume bucket was drawn and what the counters read, which
   * configuration version was live.
   *
   * This is the only truthful account of **how the connector was chosen**.
   * `routing_approach` on the payment is seeded on creation and several paths —
   * mandate, pre-routed and pinned-account among them — return before it is
   * overwritten, so it can still read `default_fallback` on a payment that was
   * routed by a rule. Read {@link RoutingDecision.decision_kind} instead.
   *
   * An attempt with `decision: null` simply has no record: the recorder is
   * best-effort, attempts that never reached routing have nothing to record,
   * and attempts predating the record exist in quantity. It is not an error.
   *
   * `GET /payments/{paymentId}/routing-decisions`
   *
   * @param paymentId - The payment intent ID.
   * @param options - Optional per-call extras: extra `headers`, a `timeout`
   *   override, and an `AbortSignal`.
   * @returns Every attempt returned for this payment, with its decision.
   *
   * @example
   * ```typescript
   * const { attempts } = await delopay.payments.routingDecisions('pay_abc123');
   * for (const attempt of attempts) {
   *   if (!attempt.decision) continue;
   *   console.log(attempt.connector, attempt.decision.decision_kind);
   * }
   * ```
   */
  async routingDecisions(
    paymentId: string,
    options?: RequestExtras,
  ): Promise<PaymentRoutingDecisionsResponse> {
    return this.request(
      'GET',
      `/payments/${encodeURIComponent(paymentId)}/routing-decisions`,
      options,
    );
  }

  /**
   * Open a playback of the payment's stored checkout recording: the audited
   * act of watching. Answers the manifest — which slices exist, how long the
   * recording runs, whether a pause in it is expected — and the
   * `playback_id` every {@link Payments.replaySlice} call carries.
   *
   * A payment that was never recorded answers `200` with `slices: []` and
   * `session_id: null` — the ordinary case for every payment made before the
   * checkout started recording — not a `404`. `404` means the payment does not
   * exist. Fetch the slices in `sequence` order; a number missing from the
   * list is a gap, not the end.
   *
   * A `POST`, because it writes one audit row: the SDK retries `GET` on its
   * own, and a lost response must not open three playbacks. Calling this
   * again deliberately opens another playback.
   *
   * Requires the `PaymentReplay` permission on a dashboard session in addition
   * to payment read access.
   *
   * `POST /payments/{paymentId}/replay/playback`
   *
   * @param paymentId - The payment intent ID.
   * @param options - Optional per-call extras: extra `headers`, a `timeout`
   *   override, and an `AbortSignal`.
   */
  async openReplayPlayback(
    paymentId: string,
    options?: RequestExtras,
  ): Promise<ReplayPlaybackResponse> {
    return this.request(
      'POST',
      `/payments/${encodeURIComponent(paymentId)}/replay/playback`,
      options,
    );
  }

  /**
   * One slice of the payment's stored checkout recording, read against an
   * open playback: the rrweb events the checkout recorded under that number.
   * `playbackId` and the numbers come from {@link Payments.openReplayPlayback};
   * a `playbackId` that names no playback of this payment opened by this
   * caller, and a number the manifest does not list, both answer `404`.
   * Not audited per slice — the playback was.
   *
   * `GET /payments/{paymentId}/replay/playback/{playbackId}/slices/{sequence}`
   *
   * @param paymentId - The payment intent ID.
   * @param playbackId - The playback, from `openReplayPlayback`.
   * @param sequence - The slice number, from the playback's manifest.
   * @param options - Optional per-call extras: extra `headers`, a `timeout`
   *   override, and an `AbortSignal`.
   */
  async replaySlice(
    paymentId: string,
    playbackId: string,
    sequence: number,
    options?: RequestExtras,
  ): Promise<ReplaySliceResponse> {
    return this.request(
      'GET',
      `/payments/${encodeURIComponent(paymentId)}/replay/playback/${encodeURIComponent(playbackId)}/slices/${encodeURIComponent(String(sequence))}`,
      options,
    );
  }

  /**
   * Soft-delete a payment. Only payments whose status is in the merchant's
   * delete policy (see {@link Payments.getDeletePolicy}) can be deleted;
   * anything else fails with a precondition error.
   *
   * `DELETE /payments/{paymentId}`
   */
  async delete(paymentId: string, options?: RequestExtras): Promise<PaymentsDeleteResponse> {
    return this.request('DELETE', `/payments/${encodeURIComponent(paymentId)}`, options);
  }

  /**
   * The effective deletable-status set for the calling merchant — lets a
   * dashboard show the delete action only where it is allowed.
   *
   * `GET /payments/delete-policy`
   */
  async getDeletePolicy(options?: RequestExtras): Promise<PaymentsDeletePolicyResponse> {
    return this.request('GET', '/payments/delete-policy', options);
  }

  // --- Advanced operations (Task 3.2) ---

  /** Generate session tokens. `POST /payments/session-tokens` */
  async sessionTokens(params: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request('POST', '/payments/session-tokens', { body: params });
  }

  /** Retrieve payment with gateway credentials. `POST /payments/sync` */
  async sync(params: Record<string, unknown>): Promise<PaymentResponse> {
    return this.request('POST', '/payments/sync', { body: params });
  }

  /** Cancel after partial capture. `POST /payments/{paymentId}/cancel-post-capture` */
  async cancelPostCapture(
    paymentId: string,
    params?: Record<string, unknown>,
  ): Promise<PaymentResponse> {
    return this.request('POST', `/payments/${encodeURIComponent(paymentId)}/cancel-post-capture`, {
      body: params,
    });
  }

  /** Incrementally authorize more funds. `POST /payments/{paymentId}/incremental-authorization` */
  async incrementalAuthorization(
    paymentId: string,
    params: Record<string, unknown>,
  ): Promise<PaymentResponse> {
    return this.request(
      'POST',
      `/payments/${encodeURIComponent(paymentId)}/incremental-authorization`,
      {
        body: params,
      },
    );
  }

  /** Extend authorization window. `POST /payments/{paymentId}/extend-authorization` */
  async extendAuthorization(
    paymentId: string,
    params?: Record<string, unknown>,
  ): Promise<PaymentResponse> {
    return this.request('POST', `/payments/${encodeURIComponent(paymentId)}/extend-authorization`, {
      body: params,
    });
  }

  /** Complete authorization. `POST /payments/{paymentId}/complete-authorize` */
  async completeAuthorize(
    paymentId: string,
    params?: Record<string, unknown>,
  ): Promise<PaymentResponse> {
    return this.request('POST', `/payments/${encodeURIComponent(paymentId)}/complete-authorize`, {
      body: params,
    });
  }

  /** Dynamic tax calculation. `POST /payments/{paymentId}/calculate-tax` */
  async calculateTax(
    paymentId: string,
    params: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request('POST', `/payments/${encodeURIComponent(paymentId)}/calculate-tax`, {
      body: params,
    });
  }

  /** Update payment metadata. `POST /payments/{paymentId}/update-metadata` */
  async updateMetadata(
    paymentId: string,
    params: Record<string, unknown>,
  ): Promise<PaymentResponse> {
    return this.request('POST', `/payments/${encodeURIComponent(paymentId)}/update-metadata`, {
      body: params,
    });
  }

  /** Retrieve extended card info. `GET /payments/{paymentId}/extended-card-info` */
  async extendedCardInfo(paymentId: string): Promise<Record<string, unknown>> {
    return this.request('GET', `/payments/${encodeURIComponent(paymentId)}/extended-card-info`);
  }

  // --- OLAP extensions (Task 4.2) ---

  /** List payments (profile-scoped). `GET /payments/profile/list` */
  async listByProfile(params?: PaymentListParams): Promise<PaymentListResponse> {
    return this.request('GET', '/payments/profile/list', {
      query: params as Record<string, string | number | undefined>,
    });
  }

  /** List payments across all shops. `GET /payments/list-all-shops` */
  async listAllShops(params?: PaymentListParams): Promise<PaymentListResponse> {
    return this.request('GET', '/payments/list-all-shops', {
      query: params as Record<string, string | number | undefined>,
    });
  }

  /** List payments by filter (POST body). `POST /payments/list` */
  async listByFilter(params: PaymentListFilterConstraints): Promise<PaymentListFilteredResponse> {
    return this.request('POST', '/payments/list', { body: params });
  }

  /**
   * List payments by filter, scoped to the caller's profile (the shop-user
   * twin of `listByFilter`). The backend narrows to the profile from the
   * auth context, so `profile_id` / `project_id` must not be sent — and
   * neither may `connector_settlement_status`, which this route refuses
   * (403): a shop-scoped viewer is not shown the settlement state, and the
   * type leaves the key out so it cannot be probed through `total_count`.
   *
   * Not to be confused with {@link Payments.listByProfile}, which is the GET
   * cursor variant and rejects this body.
   *
   * `POST /payments/profile/list`
   */
  async listByProfileFilter(
    params: ProfilePaymentListFilterConstraints,
    options?: RequestExtras,
  ): Promise<PaymentListFilteredResponse> {
    return this.request('POST', '/payments/profile/list', { body: params, ...options });
  }

  /** Get payment filter options. `GET /payments/filter` */
  async getFilters(
    params?: Record<string, string | number | undefined>,
  ): Promise<PaymentListFiltersExt> {
    return this.request('GET', '/payments/filter', { query: params });
  }

  /**
   * Get payment filter options, scoped to the caller's profile.
   * `GET /payments/profile/filter`
   */
  async getFiltersByProfile(
    params?: Record<string, string | number | undefined>,
  ): Promise<PaymentListFiltersExt> {
    return this.request('GET', '/payments/profile/filter', { query: params });
  }

  /** Get payment aggregates. `GET /payments/aggregate` */
  async aggregate(
    params?: Record<string, string | number | undefined>,
  ): Promise<Record<string, unknown>> {
    return this.request('GET', '/payments/aggregate', { query: params });
  }

  /** Get payment aggregates (profile-scoped). `GET /payments/profile/aggregate` */
  async aggregateByProfile(
    params?: Record<string, string | number | undefined>,
  ): Promise<Record<string, unknown>> {
    return this.request('GET', '/payments/profile/aggregate', { query: params });
  }

  /** Manually update payment status. `PUT /payments/{paymentId}/manual-update` */
  async manualUpdate(paymentId: string, params: Record<string, unknown>): Promise<PaymentResponse> {
    return this.request('PUT', `/payments/${encodeURIComponent(paymentId)}/manual-update`, {
      body: params,
    });
  }

  /** Approve a payment waiting for review. `POST /payments/{paymentId}/approve` */
  async approve(paymentId: string, params?: Record<string, unknown>): Promise<PaymentResponse> {
    return this.request('POST', `/payments/${encodeURIComponent(paymentId)}/approve`, {
      body: params,
    });
  }

  /** Reject a payment waiting for review. `POST /payments/{paymentId}/reject` */
  async reject(paymentId: string, params?: Record<string, unknown>): Promise<PaymentResponse> {
    return this.request('POST', `/payments/${encodeURIComponent(paymentId)}/reject`, {
      body: params,
    });
  }

  /** Initiate external 3DS authentication. `POST /payments/{paymentId}/3ds/authentication` */
  async threeDsAuthentication(
    paymentId: string,
    params: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request('POST', `/payments/${encodeURIComponent(paymentId)}/3ds/authentication`, {
      body: params,
    });
  }
}
